import { Footer } from "antd/es/layout/layout";
import { Alert, Button, Divider, Dropdown, Input, InputNumber, Modal, Select, Space, Spin, Tabs } from "antd";
import { PlusOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import ContentieuxButton from "./ContentieuxButton";
import { useBreadcrumbTitle } from "../../../contexts/BreadcrumbContext";
import { LOGGED_USER } from "../../../variables/constants";
import { rightsService } from "../../../services/rightsService";
import axiosInstance from "../../../variables/api/axiosInstance";
import InventaireSelectField from "../../../components/inventaires/InventaireSelectField";
import { getActiveInventaireCodeId } from "../../../variables/activeInventaire";
import { searchSinistre } from "../../../hooks/useSearch";
import { highlightMatch, estGarantieRC, libelleGarantie } from "../../../variables/stringFunctions";
import { frenchDate } from "../../../variables/functions";
import { displayNumbers } from "../../../variables/numbersFunctions";
import { cumulPaiementsSinistre, estPaiementCompte } from "../../../variables/cumulReglements";

// Sections réutilisées de la V1 (câblage identique à Details.js).
import DeclarationTab from "./tabsContent/sections/DeclarationTab";
import FicheDeclarationTab from "./tabsContent/sections/FicheDeclarationTab";
import VictimesTab from "./tabsContent/sections/VictimesTab";
import AdversairesTab from "./tabsContent/sections/AdversairesTab";
import SapTab from "./tabsContent/sections/SapTab";
import ReglementsTabV2 from "./tabsContent/sections/ReglementsTabV2";
import ExpertsTab from "./tabsContent/sections/ExpertsTab";
import PrisesEnChargeTab from "./tabsContent/sections/PrisesEnChargeTab";
import OffresSection from "./tabsContent/sections/OffresSection";
import DocumentsAFournir from "./tabsContent/DocumentsAFournir";
import DiscussionsTabV2 from "./tabsContent/sections/DiscussionsTabV2";
import HistoriqueTab from "./tabsContent/sections/HistoriqueTab";
import ConseilJuridiqueTab from "./tabsContent/sections/ConseilJuridiqueTab";
import InvestigateurTab from "./tabsContent/sections/InvestigateurTab";
import CommissionTechniqueTab from "./tabsContent/sections/CommissionTechniqueTab";
import Recours from "./tabsContent/Recours";
import DetailsSinistreTour from "./DetailsSinistreTour";
// Modales d'actions dossier (font DÉJÀ les vrais appels API : updateSinistre / renumeroterSinistre)
import { EditModal } from "./tabsContent/sections/components/EditModal";
import { ResponsabilityModal } from "./tabsContent/sections/components/ResponsabilityModal";
import { ChangeTypeModal } from "./tabsContent/sections/components/ChangeTypeModal";
import { validateDossierSinistre, refuseDossierSinistre } from "../../../hooks/useHooks";

const initEvaluation = {
    ancienneProvision: 0, nouvelleProvision: 0, motifRevision: null, reviseur: "", sinistre: "", provisionGaranties: [],
};
const initVictime = {
    descriptionDegatsVictime: "", sinistre: "", personne: { nom: "", prenom: "", adresse: "", profession: "", temoin: true },
};

const STATUT_MAP = {
    A: { label: "En attente de validation", cls: "info" },
    RF: { label: "Refusé", cls: "crit" },
    O: { label: "Ouvert", cls: "ok" },
    I: { label: "En instance", cls: "warn" },
    T: { label: "Terminé", cls: "ok" },
    S: { label: "Sans suite", cls: "default" },
    R: { label: "Réouvert", cls: "navy" },
};

// État d'un décaissement → libellé + variante de pastille.
const ETAT_DEC = {
    ENCAISSE: { label: "Encaissé", cls: "ok" },
    EN_ATTENTE_D_ENCAISSEMENT: { label: "En attente d'encaissement", cls: "warn" },
    VALIDE: { label: "Validé · BAP", cls: "info" },
    EN_ATTENTE: { label: "En attente", cls: "default" },
    CHEQUE_ANNULE: { label: "Chèque annulé", cls: "crit" },
    REJETE: { label: "Rejeté", cls: "default" },
};

const DSV2_CSS = `
.dsv2{--navy:#242166;--navy-2:#4B45C6;--navy-tint:#EDECF8;--ink:#181A2C;--muted:#636A85;
  --faint:#9BA0B8;--line:#E7E8F2;--line-2:#EFF0F7;--ok:#0E9F6E;--ok-tint:#E4F5EE;
  --warn:#C77A08;--warn-tint:#FBF0DC;--crit:#DC2626;--crit-tint:#FBE7E7;--info:#1D4FD8;--info-tint:#E6EDFD;
  --shadow:0 1px 2px rgba(24,26,44,.04),0 4px 16px -6px rgba(24,26,44,.10);color:var(--ink);font-size:14px}
.dsv2 .num{font-variant-numeric:tabular-nums}
.dsv2 .mono{font-family:ui-monospace,"SF Mono",Menlo,Consolas,monospace;font-variant-numeric:tabular-nums}
.dsv2-loading{display:grid;place-items:center;padding:80px 0}
.dsv2-pill{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:650;padding:4px 10px;border-radius:999px;line-height:1;white-space:nowrap}
.dsv2-pill .led{width:6px;height:6px;border-radius:50%;background:currentColor}
.dsv2-pill.ok{color:#0B7C55;background:var(--ok-tint)}
.dsv2-pill.warn{color:#9A5E06;background:var(--warn-tint)}
.dsv2-pill.crit{color:#B91C1C;background:var(--crit-tint)}
.dsv2-pill.info{color:#1D4FD8;background:var(--info-tint)}
.dsv2-pill.navy{color:var(--navy);background:var(--navy-tint)}
.dsv2-pill.default{color:var(--muted);background:#EEF0F5}
.dsv2-chip{display:inline-flex;align-items:center;gap:6px;font-size:11.5px;font-weight:600;color:var(--muted);border:1px solid var(--line);border-radius:999px;padding:3px 9px;background:#fff}
.dsv2-hero{background:#fff;border:1px solid var(--line);border-radius:14px;box-shadow:var(--shadow);padding:18px 20px;display:flex;gap:20px;align-items:flex-start;flex-wrap:wrap}
.dsv2-hero .idcol{flex:1;min-width:240px}
.dsv2-idline{display:flex;align-items:center;gap:9px;flex-wrap:wrap;margin-bottom:9px}
.dsv2-ref{font-family:ui-monospace,Menlo,monospace;font-size:13px;font-weight:600;color:var(--navy);background:var(--navy-tint);padding:4px 9px;border-radius:7px}
.dsv2-hero h1{margin:0;font-size:24px;font-weight:750;letter-spacing:-.01em;line-height:1.12}
.dsv2-subid{color:var(--muted);font-size:13px;margin-top:5px;display:flex;gap:8px;flex-wrap:wrap;align-items:center}
.dsv2-subid .sep{color:var(--faint)}
.dsv2-actions{display:flex;gap:8px;align-items:center;flex-wrap:wrap}
.dsv2-btn{display:inline-flex;align-items:center;gap:7px;font-size:13px;font-weight:600;border-radius:9px;padding:9px 14px;border:1px solid var(--line);background:#fff;color:var(--ink);cursor:pointer;transition:background .12s,border-color .12s}
.dsv2-btn:hover{background:#F7F8FC;border-color:#D8DAE8}
.dsv2-btn.primary{background:var(--navy);border-color:var(--navy);color:#fff}
.dsv2-btn.primary:hover{background:#2E2A7D}
.dsv2-kpis{display:grid;grid-template-columns:repeat(6,1fr);gap:12px;margin-top:14px}
.dsv2-kpi{background:#fff;border:1px solid var(--line);border-radius:12px;padding:13px 14px;box-shadow:var(--shadow)}
.dsv2-kpi .k-lab{font-size:11px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;color:var(--faint)}
.dsv2-kpi .k-val{font-size:19px;font-weight:750;margin-top:6px;letter-spacing:-.01em}
.dsv2-kpi .k-val small{font-size:12px;font-weight:600;color:var(--muted);margin-left:2px}
.dsv2-kpi .k-meta{font-size:11.5px;margin-top:4px;color:var(--muted)}
.dsv2-kpi .up{color:var(--ok);font-weight:700}
.dsv2-kpi.accent{background:linear-gradient(180deg,#2A2777,#242166);border-color:#242166}
.dsv2-kpi.accent .k-lab{color:#B7B4EA}.dsv2-kpi.accent .k-val{color:#fff}.dsv2-kpi.accent .k-meta{color:#C9C7EE}
.dsv2-reserve{background:#fff;border:1px solid var(--line);border-radius:12px;box-shadow:var(--shadow);padding:15px 18px;margin-top:12px}
.dsv2-rtop{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:11px;gap:12px;flex-wrap:wrap}
.dsv2-rtop .t{font-size:12.5px;font-weight:700;letter-spacing:.03em;text-transform:uppercase;color:var(--muted)}
.dsv2-rtop .q{font-size:12.5px;color:var(--muted)}.dsv2-rtop .q b{color:var(--ink);font-weight:700}
.dsv2-track{height:14px;border-radius:8px;background:#EEF0F7;display:flex;overflow:hidden;border:1px solid var(--line-2)}
.dsv2-track>span{display:block;height:100%}
.dsv2-seg-paye{background:var(--navy)}.dsv2-seg-cours{background:#6E68D8}.dsv2-seg-dispo{background:#C7CAE6}
.dsv2-legend{display:flex;gap:18px;margin-top:11px;flex-wrap:wrap}
.dsv2-legend .li{display:flex;align-items:center;gap:7px;font-size:12px;color:var(--muted)}
.dsv2-legend .sw{width:11px;height:11px;border-radius:3px}.dsv2-legend b{color:var(--ink);font-weight:700}
.dsv2-body{display:grid;grid-template-columns:320px 1fr;gap:16px;margin-top:16px;align-items:start}
.dsv2-card{background:#fff;border:1px solid var(--line);border-radius:12px;box-shadow:var(--shadow)}
.dsv2-card .hd{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:13px 16px;border-bottom:1px solid var(--line-2)}
.dsv2-card .hd .t{font-size:12.5px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:var(--muted)}
.dsv2-card .hd a{font-size:12px;font-weight:600;color:var(--navy-2);cursor:pointer}
.dsv2-card .bd{padding:15px 16px}
.dsv2-rail{display:flex;flex-direction:column;gap:14px;position:sticky;top:12px}
.dsv2-kv{display:flex;flex-direction:column;gap:11px}
.dsv2-kv .row{display:flex;justify-content:space-between;gap:12px;align-items:baseline}
.dsv2-kv .row .lab{font-size:12.5px;color:var(--muted)}
.dsv2-kv .row .val{font-size:13px;font-weight:600;text-align:right}
.dsv2-steps{display:flex;flex-direction:column}
.dsv2-step{display:grid;grid-template-columns:20px 1fr;gap:11px;padding-bottom:15px;position:relative}
.dsv2-step:last-child{padding-bottom:0}
.dsv2-step .marker{display:flex;flex-direction:column;align-items:center;position:relative}
.dsv2-step .mk{width:15px;height:15px;border-radius:50%;border:2.5px solid var(--line);background:#fff;z-index:1;margin-top:2px}
.dsv2-step .line{position:absolute;top:16px;bottom:-4px;width:2px;background:var(--line)}
.dsv2-step.done .mk{background:var(--ok);border-color:var(--ok)}.dsv2-step.done .line{background:var(--ok)}
.dsv2-step.current .mk{border-color:var(--navy);box-shadow:0 0 0 4px var(--navy-tint)}
.dsv2-step .s-t{font-size:13px;font-weight:650}.dsv2-step.pending .s-t{color:var(--faint)}
.dsv2-step .s-m{font-size:11.5px;color:var(--muted);margin-top:2px}
.dsv2-parties{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.dsv2-pcell{border:1px solid var(--line);border-radius:9px;padding:10px 11px;background:#FCFCFE}
.dsv2-pcell .n{font-size:21px;font-weight:750;letter-spacing:-.02em}.dsv2-pcell .l{font-size:11px;color:var(--muted);margin-top:1px}
.dsv2-pcell.warn{background:var(--crit-tint);border-color:#F2C7C7}.dsv2-pcell.warn .n{color:#B91C1C}
/* Origine de la donnee : « Cree(s) » = parties saisies dans le dossier ; « Declare » = nombres
   issus de la declaration de sinistre (non rattaches a des fiches individuelles). */
.dsv2-pcell .tg{display:inline-block;margin-top:5px;padding:1px 7px;border-radius:999px;font-size:9.5px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;border:1px solid var(--line);background:#F4F5F9;color:var(--muted)}
.dsv2-pcell.warn .tg{background:#FBEAEA;border-color:#F2C7C7;color:#B91C1C}
.dsv2-main{display:flex;flex-direction:column;gap:14px;min-width:0}
.dsv2-tabbar{display:flex;gap:4px;background:#fff;border:1px solid var(--line);border-radius:12px;padding:6px;box-shadow:var(--shadow);overflow-x:auto}
.dsv2-tab{display:inline-flex;align-items:center;gap:7px;white-space:nowrap;font-size:13px;font-weight:600;color:var(--muted);padding:9px 14px;border-radius:8px;border:none;background:transparent;cursor:pointer}
.dsv2-tab:hover{background:#F5F6FC;color:var(--ink)}
.dsv2-tab.active{background:var(--navy);color:#fff}
.dsv2-tab .cnt{font-size:11px;font-weight:700;padding:1px 6px;border-radius:999px;background:var(--navy-tint);color:var(--navy)}
.dsv2-tab.active .cnt{background:rgba(255,255,255,.2);color:#fff}
.dsv2-grid2{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.dsv2-fin{display:flex;flex-direction:column;gap:13px}
.dsv2-frow{display:grid;grid-template-columns:120px 1fr auto;gap:12px;align-items:center}
.dsv2-frow .fl{font-size:12.5px;color:var(--muted)}
.dsv2-frow .fbar{height:9px;border-radius:6px;background:#EEF0F7;overflow:hidden}
.dsv2-frow .fbar>i{display:block;height:100%;border-radius:6px}
.dsv2-frow .fv{font-size:13px;font-weight:700;text-align:right;min-width:96px}
.dsv2-prose{margin:0 0 12px;font-size:13.5px;line-height:1.55;color:#2A2D42}
.dsv2-twrap{overflow-x:auto}
table.dsv2-rt{width:100%;border-collapse:collapse;font-size:13px;min-width:620px}
table.dsv2-rt th{font-size:11px;font-weight:700;letter-spacing:.03em;text-transform:uppercase;color:var(--faint);text-align:left;padding:0 14px 10px;border-bottom:1px solid var(--line)}
table.dsv2-rt td{padding:12px 14px;border-bottom:1px solid var(--line-2);vertical-align:middle}
table.dsv2-rt tbody tr:hover{background:#FAFAFE}
.dsv2-benef{display:flex;align-items:center;gap:10px}
.dsv2-benef .av{width:30px;height:30px;border-radius:8px;background:var(--navy-tint);color:var(--navy);display:grid;place-items:center;font-weight:700;font-size:12px;flex-shrink:0}
.dsv2-benef .nm{font-weight:650;font-size:13px}
.dsv2-benef .rg{font-size:11px;color:var(--faint);font-family:ui-monospace,Menlo,monospace}
.dsv2-amt{font-weight:700;text-align:right}.dsv2-amt.neg{color:var(--crit)}
.dsv2-cheque{font-family:ui-monospace,Menlo,monospace;font-size:12px}.dsv2-cheque .bk{color:var(--faint);font-size:11px}
.dsv2-tfoot{font-weight:750;background:#FAFAFC}
.dsv2-tl{display:flex;flex-direction:column}
.dsv2-ev{display:grid;grid-template-columns:15px 1fr;gap:13px;padding-bottom:16px;position:relative}
.dsv2-ev:last-child{padding-bottom:0}
.dsv2-ev .em{width:11px;height:11px;border-radius:50%;background:#fff;border:2.5px solid var(--navy);margin-top:3px;z-index:1}
.dsv2-ev.money .em{border-color:var(--ok)}.dsv2-ev.warn .em{border-color:var(--warn)}
.dsv2-ev .eline{position:absolute;left:5px;top:15px;bottom:-3px;width:2px;background:var(--line)}
.dsv2-ev .et{font-size:13px;font-weight:600}
.dsv2-ev .ed{font-size:11.5px;color:var(--muted);margin-top:2px;display:flex;gap:10px;flex-wrap:wrap}
.dsv2-empty{padding:26px 10px;text-align:center;color:var(--faint);font-size:13px}
@media (max-width:1100px){.dsv2-kpis{grid-template-columns:repeat(3,1fr)}.dsv2-body{grid-template-columns:1fr}.dsv2-rail{position:static}.dsv2-grid2{grid-template-columns:1fr}}
@media (max-width:560px){.dsv2-kpis{grid-template-columns:repeat(2,1fr)}.dsv2-hero h1{font-size:21px}}
@media (prefers-reduced-motion:reduce){.dsv2 *{transition:none!important}}
`;

const DetailsSinistreV2 = () => {
    const { setPageTitle } = useBreadcrumbTitle();
    const param = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const data = location?.state;
    // codeId via location.state (navigation interne) OU query param ?codeId= (deep-link, ex. clic
    // sur une notification de mention qui redirige vers /details-sinistre/v2?codeId=...&tab=...).
    const codeId = data?.codeId || new URLSearchParams(location?.search || "").get("codeId");

    const [sinistre, setSinistre] = useState();
    const [captures, setCaptures] = useState([]);
    const [captured, setCaptured] = useState([]);
    const [modalReevaluation, setModalReevaluation] = useState(false);
    const [modalRecoursReevaluation, setModalRecoursReevaluation] = useState(false);
    const [modalVictime, setModalVictime] = useState(false);
    const [evaluation, setEvaluation] = useState(initEvaluation);
    const [editMode, setEditMode] = useState(false);
    const [evaluations, setEvaluations] = useState([]);
    const [recoursEvaluations, setRecoursEvaluations] = useState([]);
    const [victime, setVictime] = useState(initVictime);
    const [victimes, setVictimes] = useState([]);
    const [modalReglementSinistre, setModalReglementSinistre] = useState(false);
    const [modalEncaissement, setModalEncaissement] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [searchValue, setSearchValue] = useState("");

    const [garanties, setGaranties] = useState([]);
    const [selectedGaranties, setSelectedGaranties] = useState([]);
    const [garantieState, setGarantieState] = useState("");
    const [valueSelected, setValueSelected] = useState([]);
    const [provisionGaranties, setProvisionGarantie] = useState([]);
    const [decaissements, setDecaissements] = useState([]);
    const [encaissements, setEncaissements] = useState([]);
    const [montantEnCours, setMontantEnCours] = useState(0);

    const [ordreDepense, setOrdreDepense] = useState("");
    const [modalOrdreDepense, setModalOrdreDepense] = useState(false);
    const [ficheDemandeReglement, setFicheDemandeReglement] = useState("");
    const [modalFicheDemandeReglement, setModalFicheDemandeReglement] = useState(false);
    const [modalAssignCheque, setModalAssignCheque] = useState(false);
    const [modalRemiseCheque, setModalRemiseCheque] = useState(false);
    const [modalAnnulerCheque, setModalAnnulerCheque] = useState(false);
    const [decaissement, setDecaissement] = useState();
    const [experts, setExperts] = useState([]);
    const [expertises, setExpertises] = useState([]);
    const [modalCloture, setModalCloture] = useState(false);
    const [modalSuspension, setModalSuspension] = useState(false);
    const [loadingCloture, setLoadingCloture] = useState(false);
    const [loadingSuspension, setLoadingSuspension] = useState(false);
    const [loadingSinistre, setLoadingSinistre] = useState(false);
    const [loadingReeval, setLoadingReeval] = useState(false);
    // Le modal V2 de réévaluation est réutilisé pour le recours ; ce flag pilote le POST.
    const [reevalRecours, setReevalRecours] = useState(false);
    const [reevalSelected, setReevalSelected] = useState([]);
    const [reevalInventaireCodeId, setReevalInventaireCodeId] = useState(null);
    const [sousEvaluations, setSousEvaluations] = useState([]);
    const [modalEdit, setModalEdit] = useState(false);
    const [modalResponsabilite, setModalResponsabilite] = useState(false);
    const [modalChangeType, setModalChangeType] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [items, setItems] = useState(["Nouvelles investigations", "Imprévus", "Clôture", "Nouveau élément dans le dossier", "Forclusion", "Provision insuffisante", "Inventaire"]);

    // Onglet actif PERSISTÉ (sessionStorage) : après un enregistrement suivi d'une réactualisation
    // (re-fetch ou navigation même-route), on revient sur le MÊME onglet plutôt que « Aperçu ».
    const TAB_STORAGE_KEY = "dsv2_activeTab";
    const [activeTab, setActiveTab] = useState(() => {
        try { return sessionStorage.getItem(TAB_STORAGE_KEY) || "apercu"; } catch { return "apercu"; }
    });
    useEffect(() => {
        try { sessionStorage.setItem(TAB_STORAGE_KEY, activeTab); } catch { /* stockage indisponible */ }
    }, [activeTab]);
    // Deep-link : ?tab=discussions (ex. clic sur une notification de mention) ouvre l'onglet ciblé.
    useEffect(() => {
        const tab = new URLSearchParams(location.search).get("tab");
        if (tab) setActiveTab(tab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);
    const [showTour, setShowTour] = useState(false);
    const [refetch, setRefetch] = useState(false);
    const [motifRevisionState, setMotifRevisionState] = useState("");
    const [isRecours, setIsRecours] = useState(false);
    const [quittance, setQuittance] = useState();
    const [modalQuittance, setModalQuittance] = useState(false);
    const [montant, setMontant] = useState(0);
    const [montantPaye, setMontantPaye] = useState(0);
    const [name, setName] = useState("");
    const inputRef = useRef(null);

    const showQuittance = (item) => { setQuittance(item); setModalQuittance(true); };

    useEffect(() => {
        const label = sinistre?.numeroSinistreM || sinistre?.numeroSinistreP || sinistre?.numSinistre;
        if (label) setPageTitle(label);
        return () => setPageTitle(null);
    }, [sinistre]);

    useEffect(() => {
        if (codeId) {
            setLoadingSinistre(true);
            axiosInstance.get(`/api/sinistre/codeId/${codeId}`)
                .then((res) => {
                    setSinistre(res.data);
                    // Évaluations NORMALES : hors encaissement ET hors recours (le recours a son
                    // propre onglet, filtré ci-dessous) — sinon une réévaluation de recours
                    // (encaissement=false) polluerait la piste normale.
                    // On EXCLUT les évaluations historisées (activeData=false) : elles créent des
                    // doublons dans l'historique des réévaluations (chaque version a son propre codeId,
                    // donc la déduplication par codeId ne les élimine pas).
                    setEvaluations(res.data?.evaluations?.filter((d) => d.activeData !== false && (d.encaissement === false || d.encaissement === null) && d.recours !== true));
                    setRecoursEvaluations(res.data?.evaluations?.filter((d) => d.activeData !== false && d.recours === true));
                    setVictimes(res.data?.victimes);
                    setDecaissements((res.data?.decaissements || []).filter((d) => d.encaissement !== true)
                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
                    setEncaissements((res.data?.decaissements || []).filter((d) => d.encaissement === true)
                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
                    setExperts(res.data?.experts);
                    let mEnCours = 0.0;
                    const allDecs = res.data?.decaissements || [];

                    // Réglé (net) = règle CANONIQUE (cumulReglements.js / SQL) : chèque assigné
                    // (EN_ATTENTE_D_ENCAISSEMENT/ENCAISSE) SANS annulation de chèque active portant le
                    // même n° + banque. Un simple cumul par état laissait la ligne positive comptée
                    // alors que sa contre-passation CHEQUE_ANNULE (−montant) était ignorée → l'annulation
                    // d'un chèque ne réduisait ni le « réglé » ni la consommation de la provision.
                    const m = cumulPaiementsSinistre(allDecs);

                    // Payé = ENCAISSÉ effectif, hors chèques annulés (même règle canonique).
                    const mt = allDecs
                        .filter((d) => d.etatDecaissement === "ENCAISSE" && estPaiementCompte(d, allDecs))
                        .reduce((s, d) => s + (parseFloat(d.montantGlobal) || 0), 0);

                    // En cours = VALIDE sans numéro de chèque (demande non encore payée), montant > 0.
                    allDecs.forEach((d) => {
                        if (d.encaissement) return; // recours → ignoré ici
                        if (d.etatDecaissement === "VALIDE" && !d.numeroCheque && (parseFloat(d.montantGlobal) || 0) > 0) {
                            mEnCours += parseFloat(d.montantGlobal) || 0;
                        }
                    });

                    setMontant(m);
                    setMontantPaye(mt);
                    setMontantEnCours(mEnCours);

                    // Engin de référence pour les garanties : celui du SINISTRE ; s'il est null (dossier
                    // sans engin rattaché), on prend l'engin du CONTRAT lié au sinistre.
                    const enginRef = res.data?.engin
                        || res.data?.contrat?.engin
                        || res.data?.contrat?.engins?.[0];
                    if (enginRef) {
                        axiosInstance.get(`/api/garantie/categorie/compulsory/code/${enginRef?.categorie?.codeCategorie}`)
                            .then((response) => {
                                let g = [];
                                enginRef?.sousGaranties && enginRef?.sousGaranties?.forEach((sg) => {
                                    g.push({ value: sg.garantie.codeId, label: sg.nom, garantie: sg.garantie });
                                });
                                response?.data && response?.data?.forEach((ga) => {
                                    g.push({ value: ga.codeId, label: ga.nom, garantie: ga });
                                });
                                setGaranties(g);
                            })
                            .catch((err) => { console.log(err); });
                    }
                    if (res.data?.captures) {
                        setCaptures(res.data?.captures);
                        axiosInstance.get(`/api/sinistre/captures/${res.data.codeId}`)
                            .then((resp) => {
                                let cpt = [];
                                resp.data?.forEach((c) => {
                                    cpt.push({ uid: c.captureId, name: c.nom, status: "done", url: `data:image/png;base64,${c}` });
                                });
                                setCaptured(cpt);
                            })
                            .catch((err) => { console.log(err); });
                    }
                    setLoadingSinistre(false);
                })
                .catch((err) => { setLoadingSinistre(false); console.log(err); });

            axiosInstance.get(`/api/expertise/sinistre/${codeId}`)
                .then((res) => { setExpertises(res.data); })
                .catch((err) => { console.log(err); });
        }
    }, [param, refetch, codeId]);

    const handleDecaissementAfterUpdate = (updatedDecaissement) => {
        if (!updatedDecaissement || !updatedDecaissement.codeId) return;
        setDecaissements((previous) => Array.isArray(previous)
            ? previous?.map((item) => item.codeId === updatedDecaissement.codeId ? { ...item, ...updatedDecaissement } : item) : previous);
        setEncaissements((previous) => Array.isArray(previous)
            ? previous?.map((item) => item.codeId === updatedDecaissement.codeId ? { ...item, ...updatedDecaissement } : item) : previous);
        setSinistre((previous) => {
            if (!previous || !Array.isArray(previous?.decaissements)) return previous;
            const decaissementsMaj = previous?.decaissements?.map((item) =>
                item.codeId === updatedDecaissement.codeId ? { ...item, ...updatedDecaissement } : item);
            return { ...previous, decaissements: decaissementsMaj };
        });
    };
    const isValidated = () => {
        if (motifRevisionState === "success" && garantieState === "success") return true;
        if (motifRevisionState !== "success") return false;
        if (garantieState !== "success") return false;
    };
    const handleSubmit = (e, rec) => {
        e.preventDefault();
        if (isValidated()) {
            evaluation.sinistre = sinistre;
            evaluation.reviseur = LOGGED_USER;
            evaluation.ancienneProvision = sinistre?.evaluation;
            evaluation.encaissement = isRecours;
            evaluation.encaissement = rec;
            // Garde (piste NORMALE) : la provision ne peut pas descendre sous le cumul des règlements
            // déjà effectués (backend l'impose aussi ; message immédiat ici).
            if (!rec && Number(evaluation.nouvelleProvision || 0) < regleVal - 0.5) {
                toast.error(`La nouvelle provision (${fmt(evaluation.nouvelleProvision)} F) ne peut pas être inférieure au cumul des règlements déjà effectués (${fmt(regleVal)} F).`);
                return;
            }
            const method = editMode ? "put" : "post";
            axiosInstance.post(`/api/provision-garantie/all`, JSON.stringify(provisionGaranties))
                .then((response) => {
                    evaluation.provisionGaranties = response.data;
                    axiosInstance[method](`/api/evaluation`, JSON.stringify(evaluation))
                        .then(() => {
                            setEditMode(false);
                            setModalReevaluation(false);
                            setModalRecoursReevaluation(false);
                            setEvaluation(initEvaluation);
                            toast(editMode ? "Mis à jour" : "créée");
                            setRefetch((r) => !r);
                        })
                        .catch((err) => { console.log(err); });
                })
                .catch((err) => { console.log(err); });
        } else {
            toast("Vérifier vos entrées");
        }
    };
    const handleChange = (e, nom) => {
        if (nom) { setEvaluation({ ...evaluation, [nom]: e }); }
        else { const { name: n, value } = e.target; setEvaluation({ ...evaluation, [n]: value }); }
    };
    const setOpenReglement = (value) => { setModalReglementSinistre(value); };
    const handleSuspensionDossier = () => {
        setLoadingSuspension(true);
        axiosInstance.get(`/api/sinistre/suspension/${sinistre.codeId}`)
            .then((res) => {
                setLoadingSuspension(false); setModalSuspension(false);
                toast.success(sinistre?.sortDossier === "S" ? "Dossier réactivé avec succès!" : "Dossier suspendu avec succès!");
                navigate(`/details-sinistre/v2`, { state: { codeId: res.data.codeId } });
            })
            .catch((err) => {
                console.log(err); setLoadingSuspension(false); setModalSuspension(false);
                toast.error(sinistre?.sortDossier === "S" ? "Échec lors de la réouverture du dossier!" : "Échec lors de la suspension du dossier!");
            });
    };
    // Réactivation / réouverture d'un dossier CLÔTURÉ (Terminé/Sans suite/Forclose/Refusé) → statut
    // « Rouvert » (R). Habilitation HSI104 imposée côté serveur (403 sinon → message).
    const handleReouvertureDossier = (motif) => {
        return axiosInstance.get(`/api/sinistre/reouvrir/${sinistre.codeId}/R`, { params: { motif } })
            .then((res) => {
                toast.success("Dossier réouvert avec succès !");
                navigate(`/details-sinistre/v2`, { state: { codeId: res.data.codeId } });
            })
            .catch((err) => {
                const msg = err?.response?.data?.message
                    || (err?.response?.status === 403 ? "Réouverture soumise à l'habilitation HSI104." : "Échec de la réouverture du dossier.");
                toast.error(msg);
                return Promise.reject(err);
            });
    };
    const confirmReouverture = () => {
        let motif = "";
        Modal.confirm({
            title: "Réactiver / réouvrir le dossier ?", icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p style={{ marginBottom: 8 }}>Le dossier repassera au statut « Réouvert » et redeviendra opérable (réévaluation, règlement).</p>
                    <Input.TextArea rows={3} placeholder="Motif / observation de réouverture (obligatoire)"
                        onChange={(e) => { motif = e.target.value; }} />
                </div>
            ),
            okText: "Réouvrir", cancelText: "Annuler",
            onOk: () => {
                if (!motif || !motif.trim()) {
                    toast.warn("Veuillez préciser le motif / observation de réouverture.");
                    return Promise.reject();
                }
                return handleReouvertureDossier(motif.trim());
            },
        });
    };
    // ── VERROU « LECTURE SEULE » (HSI114, réservé Pool) ─────────────────────────
    // Pose : POST /api/sinistre/{codeId}/lecture-seule (motif obligatoire). Retrait : DELETE.
    // Le serveur impose le double contrôle (HSI114 + type organisme POOL) et bloque toute
    // écriture tant que le verrou est posé (423 LOCKED, opposable à tous, Pool inclus).
    const confirmLectureSeule = () => {
        let motif = "";
        Modal.confirm({
            title: "Mettre le dossier en lecture seule ?", icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p style={{ marginBottom: 8 }}>Le dossier sera <b>gelé pour tout le monde</b> (Pool inclus) : aucune modification (règlement, expertise, PEC, victime, évaluation, discussion…) ne sera possible tant que le verrou n'est pas levé.</p>
                    <Input.TextArea rows={3} placeholder="Motif de la mise en lecture seule (obligatoire)"
                        onChange={(e) => { motif = e.target.value; }} />
                </div>
            ),
            okText: "Verrouiller", cancelText: "Annuler", okButtonProps: { danger: true },
            onOk: () => {
                if (!motif || !motif.trim()) {
                    toast.warn("Veuillez préciser le motif de mise en lecture seule.");
                    return Promise.reject();
                }
                return axiosInstance.post(`/api/sinistre/${sinistre.codeId}/lecture-seule`, { motif: motif.trim() })
                    .then((res) => {
                        toast.success("Dossier mis en lecture seule.");
                        navigate(`/details-sinistre/v2`, { state: { codeId: res.data.codeId } });
                    })
                    .catch((err) => {
                        const msg = err?.response?.data?.message
                            || (err?.response?.status === 403 ? "Action réservée au Pool (habilitation HSI114)." : "Échec de la mise en lecture seule.");
                        toast.error(msg);
                        return Promise.reject(err);
                    });
            },
        });
    };
    const confirmLeverLectureSeule = () => {
        Modal.confirm({
            title: "Lever la lecture seule ?", icon: <ExclamationCircleOutlined />,
            content: <p>Le dossier redeviendra modifiable selon les règles habituelles.</p>,
            okText: "Déverrouiller", cancelText: "Annuler",
            onOk: () => axiosInstance.delete(`/api/sinistre/${sinistre.codeId}/lecture-seule`)
                .then((res) => {
                    toast.success("Lecture seule levée.");
                    navigate(`/details-sinistre/v2`, { state: { codeId: res.data.codeId } });
                })
                .catch((err) => {
                    const msg = err?.response?.data?.message
                        || (err?.response?.status === 403 ? "Action réservée au Pool (habilitation HSI114)." : "Échec de la levée de la lecture seule.");
                    toast.error(msg);
                    return Promise.reject(err);
                }),
        });
    };
    // ── CARTE BRUNE (BNCB) : marquer/retirer un dossier transfrontalier. Une fois marqué, le BNCB est
    //    proposé par défaut comme bénéficiaire au règlement (représentant spécial / paiement direct).
    const confirmCarteBrune = () => {
        const actif = !!sinistre?.carteBrune;
        Modal.confirm({
            title: actif ? "Retirer le marquage « Carte Brune » ?" : "Marquer ce dossier « Carte Brune » (BNCB) ?",
            icon: <ExclamationCircleOutlined />,
            content: actif
                ? <p>Le dossier ne sera plus traité comme transfrontalier ; le BNCB ne sera plus proposé par défaut au règlement.</p>
                : <p>Le dossier sera traité comme <b>transfrontalier (carte brune CEDEAO)</b> : le <b>Bureau National de la Carte Brune (BNCB)</b> sera proposé par défaut comme bénéficiaire du chèque (représentant spécial / paiement direct).</p>,
            okText: actif ? "Retirer" : "Marquer", cancelText: "Annuler",
            onOk: () => axiosInstance.put(`/api/sinistre/${sinistre.codeId}/carte-brune`, null, { params: { valeur: !actif } })
                .then((res) => {
                    toast.success(actif ? "Marquage Carte Brune retiré." : "Dossier marqué Carte Brune (BNCB).");
                    navigate(`/details-sinistre/v2`, { state: { codeId: res.data.codeId } });
                })
                .catch((err) => {
                    toast.error(err?.response?.data?.message || "Échec de l'opération Carte Brune.");
                    return Promise.reject(err);
                }),
        });
    };
    const addItem = (e) => {
        e.preventDefault();
        setItems([...items, name || `New item ${items.length + 1} `]);
        setName("");
        setTimeout(() => { inputRef.current?.focus(); }, 0);
    };
    const handleClotureDossier = () => {
        setLoadingCloture(true);
        // Clôture = statut « Terminé » → segment /T requis par l'endpoint /cloture/{codeId}/{statutDossier}
        // (sans lui : 405 Method Not Supported). "T"→Terminé (sinon Sans suite) ; l'endpoint met SAP=0.
        axiosInstance.get(`/api/sinistre/cloture/${sinistre.codeId}/T`)
            .then((res) => {
                setLoadingCloture(false); setModalCloture(false);
                toast.success("Dossier clôturé avec succès");
                navigate(`/details-sinistre/v2`, { state: { codeId: res.data.codeId } });
            })
            .catch((err) => {
                console.log(err); setLoadingCloture(false); setModalCloture(false);
                toast.error("Échec lors de la clôture du dossier");
            });
    };
    const onNameChange = (event) => { setName(event.target.value); };
    const showOrdreDepense = (item) => { setOrdreDepense(item); setModalOrdreDepense(true); };
    const editEvaluation = (item) => {
        // Même garde que la réévaluation : éditer une évaluation d'un dossier CLÔTURÉ exige HSI104.
        if (["T", "S", "F", "Rf", "RF"].includes(sinistre?.sortDossier) && !rightsService("HSI104")) {
            toast.warn("Dossier clôturé : modifier une évaluation requiert l'habilitation HSI104 (ou la réouverture du dossier).");
            return;
        }
        setEditMode(true);
        setEvaluation(item);
        const subs = (item?.sousEvaluations || []).map((s) => ({
            ...s, montantTotal: (Number(s.montantPrincipal) || 0) + (Number(s.montantHonoraires) || 0),
        }));
        setSousEvaluations(subs);
        setReevalSelected(subs.map((s) => s.garantie));
        setMotifRevisionState("success"); setGarantieState(subs.length ? "success" : "error");
        setModalReevaluation(true);
    };
    const setOpenEncaissement = (value) => { setModalEncaissement(value); };
    // ── Réévaluation V2 : Montant Principal + Honoraires par garantie (comme l'ancienne page),
    //     total par ligne + total général, motif + explication détaillée → POST /api/evaluation/reevaluation.
    // Filtrage STRICT : la réévaluation ne propose QUE les garanties concernées (cochées à la
    // déclaration, `sinistre.garanties`). Si aucune n'est renseignée, la liste est vide et
    // l'utilisateur doit d'abord les définir via « Modifier les garanties concernées » (onglet SAP).
    const reevalOptions = (() => {
        // Cas particulier RESPONSABILITÉ CIVILE : les libellés déclarés ("RC de base", "RC",
        // "Responsabilité civile"…) et le code contrat (RC/ICDB) désignent la MÊME garantie « RC »
        // (cf. estGarantieRC/libelleGarantie). On les traite comme une famille équivalente.
        const declarees = new Set((sinistre?.garanties || []).map((x) => (x || "").trim()));
        const declareesHasRC = (sinistre?.garanties || []).some(estGarantieRC);
        const seen = new Set(); const out = [];
        (garanties || []).forEach((g) => {
            const obj = g.garantie || {};
            const nom = (g.label || obj.nom || "").trim();
            const code = obj.code || "";
            const isRc = code.startsWith("RC") || code.startsWith("ICDB") || estGarantieRC(nom);
            // Filtrage STRICT : garantie RC ⇒ concernée si UNE garantie RC est déclarée ; sinon
            // correspondance exacte du nom déclaré.
            const concernee = isRc ? declareesHasRC : declarees.has(nom);
            if (!concernee) return;
            const val = isRc ? "RC" : libelleGarantie(nom);
            if (val && !seen.has(val)) { seen.add(val); out.push({ value: val, label: val }); }
        });
        return out;
    })();
    // Découpage principal/honoraires COURANT d'une garantie (label normalisé), lu dans les
    // sous-évaluations de l'évaluation courante de la piste voulue (normale ou recours). Sert à
    // pré-remplir la réévaluation avec les anciennes valeurs, garantie par garantie.
    const currentSplitForGarantie = (val, rec) => {
        const evalCour = (evaluations || [])
            .filter((e) => e && e.activeData !== false && (rec ? e.recours === true : e.recours !== true))
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
        const matches = evalCour ? (evalCour.sousEvaluations || []).filter((x) => {
            const nom = x?.garantieAffecte?.nom || x?.garantieAffectee;
            return libelleGarantie(nom) === val;
        }) : [];
        if (!matches.length) return null;
        // Somme des sous-évaluations correspondantes (utile quand un libellé normalisé, ex. « RC »,
        // regroupe plusieurs variantes).
        return matches.reduce((acc, se) => ({
            montantPrincipal: acc.montantPrincipal + (Number(se.montantPrincipal) || 0),
            montantHonoraires: acc.montantHonoraires + (Number(se.montantHonoraires) || 0),
            montantTotal: acc.montantTotal + (Number(se.montantTotal) || 0),
        }), { montantPrincipal: 0, montantHonoraires: 0, montantTotal: 0 });
    };
    // recours === true UNIQUEMENT (et pas un objet event truthy passé par onClick) : le même
    // modal V2 sert aux deux, seul le flag envoyé au backend change.
    const openReeval = (recours) => {
        // Dossier CLOS (Terminé/Sans suite/Forclose/Refusé) : toute réévaluation — NORMALE OU RECOURS —
        // exige l'habilitation HSI104 (ou la réouverture préalable). Le backend l'impose aussi.
        if (["T", "S", "F", "Rf", "RF"].includes(sinistre?.sortDossier) && !rightsService("HSI104")) {
            toast.warn("Dossier clôturé : la réévaluation requiert l'habilitation HSI104 (ou la réouverture du dossier).");
            return;
        }
        const rec = recours === true;
        setReevalRecours(rec);
        setEditMode(false); setEvaluation(initEvaluation);
        // Pré-remplit toutes les garanties concernées avec leurs valeurs COURANTES (garantie par
        // garantie) ; l'utilisateur augmente/diminue puis enregistre → nouvelle réévaluation.
        const initial = reevalOptions.map((o) => {
            const cur = currentSplitForGarantie(o.value, rec);
            return {
                garantie: o.value,
                montantPrincipal: cur?.montantPrincipal || 0,
                montantHonoraires: cur?.montantHonoraires || 0,
                montantTotal: cur?.montantTotal || 0,
            };
        });
        setReevalSelected(reevalOptions.map((o) => o.value));
        setSousEvaluations(initial);
        setReevalInventaireCodeId(getActiveInventaireCodeId()); // pré-remplit avec l'inventaire actif
        setMotifRevisionState(""); setGarantieState(reevalOptions.length ? "success" : ""); setModalReevaluation(true);
    };
    const closeReeval = () => {
        setModalReevaluation(false); setEditMode(false); setEvaluation(initEvaluation);
        setReevalSelected([]); setSousEvaluations([]); setReevalRecours(false); setReevalInventaireCodeId(null);
    };
    // Ouverture AUTOMATIQUE de la réévaluation quand on arrive depuis un blocage de plafond SAP
    // (navigate("/details-sinistre/v2", { state: { codeId, openReeval: true } })) — une seule fois,
    // après chargement du sinistre (garanties/provision disponibles dans la modale).
    const reevalAutoDone = useRef(false);
    useEffect(() => {
        if (data?.openReeval && sinistre && !reevalAutoDone.current) {
            reevalAutoDone.current = true;
            openReeval(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.openReeval, sinistre]);
    const onGarantiesChange = (values) => {
        setReevalSelected(values);
        setGarantieState(values.length ? "success" : "error");
        setSousEvaluations((prev) => values.map((v) => {
            const existing = (prev || []).find((s) => s.garantie === v);
            if (existing) return existing;
            // Nouvelle garantie ajoutée : pré-remplir avec sa valeur courante (repli à 0).
            const cur = currentSplitForGarantie(v, reevalRecours);
            return { garantie: v, montantPrincipal: cur?.montantPrincipal || 0, montantHonoraires: cur?.montantHonoraires || 0, montantTotal: cur?.montantTotal || 0 };
        }));
    };
    const onMontantChange = (index, field, value) => {
        setSousEvaluations((prev) => {
            const next = [...(prev || [])];
            if (!next[index]) return next;
            next[index] = { ...next[index], [field]: value || 0 };
            next[index].montantTotal = (Number(next[index].montantPrincipal) || 0) + (Number(next[index].montantHonoraires) || 0);
            return next;
        });
    };
    const totalGeneral = (sousEvaluations || []).reduce((a, s) => a + (Number(s?.montantTotal) || 0), 0);
    const submitReeval = () => {
        if (!reevalSelected?.length) { toast.error("Sélectionnez au moins une garantie."); return; }
        if (!evaluation?.motifRevision) { toast.error("Indiquez le motif de la révision."); return; }
        // Garde (piste NORMALE) : la nouvelle provision ne peut pas descendre sous le cumul des règlements
        // déjà effectués (backend l'impose aussi ; ici pour un message immédiat).
        if (!reevalRecours && totalGeneral < regleVal - 0.5) {
            toast.error(`La nouvelle provision (${fmt(totalGeneral)} F) ne peut pas être inférieure au cumul des règlements déjà effectués (${fmt(regleVal)} F).`);
            return;
        }
        setLoadingReeval(true);
        const ev = {
            ...evaluation, sousEvaluations, motifRevision: evaluation.motifRevision, sinistre,
            nouvelleProvision: totalGeneral,
            // Ancienne provision : sur la piste RECOURS, c'est l'évaluation recours courante
            // (0 au premier recours), PAS l'évaluation normale (le backend le refera de toute façon).
            ancienneProvision: reevalRecours ? (sinistre?.evaluationRecours || 0) : sinistre?.evaluation,
            recours: reevalRecours ? true : null, explicationMotif: evaluation.explicationMotif || null,
            inventaireCodeId: reevalInventaireCodeId || null,
        };
        axiosInstance.post(`/api/evaluation/reevaluation`, ev)
            .then(() => { setLoadingReeval(false); toast.success(reevalRecours ? "Réévaluation de recours effectuée" : "Réévaluation effectuée avec succès"); closeReeval(); setRefetch((r) => !r); })
            .catch((err) => { setLoadingReeval(false); console.log(err); toast.error("Échec de l'opération"); });
    };
    const confirmCloture = () => {
        Modal.confirm({
            title: "Clôturer le dossier ?", icon: <ExclamationCircleOutlined />,
            content: "Le dossier passera au statut « Terminé ».",
            okText: "Clôturer", okButtonProps: { danger: true }, cancelText: "Annuler",
            onOk: handleClotureDossier,
        });
    };
    const confirmSuspension = () => {
        const reactiver = sinistre?.sortDossier === "S";
        Modal.confirm({
            title: reactiver ? "Réactiver le dossier ?" : "Suspendre le dossier ?", icon: <ExclamationCircleOutlined />,
            content: reactiver ? "Le dossier sera réactivé." : "Le dossier sera classé « sans suite ».",
            okText: reactiver ? "Réactiver" : "Suspendre", cancelText: "Annuler",
            onOk: handleSuspensionDossier,
        });
    };
    const handleReevaluationClick = openReeval;

    // ── Actions dossier sur VRAIS endpoints ──
    // Valider : GET /api/sinistre/validation/{codeId}/{type}. Le 2e segment est traité comme le TYPE
    // (parseTypeSinistre) → on renvoie le type COURANT pour ne pas l'écraser ; le statut passe à OUVERT.
    const doValider = () => {
        Modal.confirm({
            title: "Valider le dossier ?", icon: <ExclamationCircleOutlined />,
            content: "Le dossier passera au statut « Ouvert » et un numéro définitif sera généré.",
            okText: "Valider", cancelText: "Annuler",
            onOk: () => validateDossierSinistre(sinistre?.codeId, sinistre?.typeSinistre || "MIXTE")
                .then((res) => { toast.success("Dossier validé"); navigate("/details-sinistre/v2", { state: { codeId: res.data.codeId } }); })
                .catch((err) => { console.log(err); toast.error("Échec de la validation"); }),
        });
    };
    const doRefuser = () => {
        Modal.confirm({
            title: "Refuser le dossier ?", icon: <ExclamationCircleOutlined />,
            content: "Le dossier sera marqué comme refusé.",
            okText: "Refuser", okButtonProps: { danger: true }, cancelText: "Annuler",
            onOk: () => refuseDossierSinistre(sinistre?.codeId)
                .then((res) => { toast.success("Dossier refusé"); navigate("/details-sinistre/v2", { state: { codeId: res.data.codeId } }); })
                .catch((err) => { console.log(err); toast.error("Échec du refus"); }),
        });
    };
    const onDossierMenu = ({ key }) => {
        if (key === "edit") setModalEdit(true);
        else if (key === "type") setModalChangeType(true);
        else if (key === "resp") setModalResponsabilite(true);
        else if (key === "valider") doValider();
        else if (key === "refuser") doRefuser();
        else if (key === "suspendre") confirmSuspension();
        else if (key === "reouvrir") confirmReouverture();
        else if (key === "lecture-seule") confirmLectureSeule();
        else if (key === "lever-lecture-seule") confirmLeverLectureSeule();
        else if (key === "carte-brune") confirmCarteBrune();
    };

    // Évaluation NORMALE courante. Le flag `courant` est peu fiable : après une réévaluation /
    // re-version du sinistre, il peut n'être posé sur AUCUNE des évaluations actives (cf.
    // backfill_evaluation_courante_et_sap.sql) → l'onglet Règlements affichait alors « Aucune
    // évaluation » à tort. On reproduit donc la règle du reste de l'app (SapTab, updateSinistreEtat) :
    // priorité au flag `courant`, sinon la PLUS RÉCENTE (createdAt, puis historiqueId). `evaluations`
    // est déjà filtré (actives, non-recours, non-encaissement).
    const evaluationCourante = useMemo(() => {
        const list = evaluations || [];
        if (list.length === 0) return null;
        const flagged = list.find((e) => e.courant === true);
        if (flagged) return flagged;
        return [...list].sort((a, b) =>
            (new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0))
            || ((b?.historiqueId || 0) - (a?.historiqueId || 0))
        )[0] || null;
    }, [evaluations]);

    // ── Valeurs dérivées pour le cockpit ──
    const fmt = (n) => displayNumbers(Math.trunc(Number(n) || 0));
    const statut = sinistre?.sortDossier;
    const st = STATUT_MAP[statut] || { label: statut || "—", cls: "default" };
    // Dossier CLOS (Terminé / Sans suite / Forclose / Refusé) : plus rien à payer → SAP/disponible = 0.
    const dossierClos = new Set(["T", "S", "F", "Rf", "RF"]).has(statut);
    // ── VERROU « LECTURE SEULE » : dossier gelé pour tous. La pose/levée est réservée au Pool
    //    (habilitation HSI114 + type organisme POOL). readOnly désactive TOUTES les actions.
    const readOnly = !!sinistre?.lectureSeule;
    const estPool = (() => {
        try {
            const u = JSON.parse(sessionStorage.getItem("user") || "null");
            return (u?.profil?.organisme?.typeOrganisme || "").toUpperCase() === "POOL";
        } catch (e) { return false; }
    })();
    const peutVerrouiller = estPool && rightsService("HSI114");
    // Traçabilité rédaction : rédacteur INITIAL (redacteurInitialNom résolu serveur depuis created_by,
    // repli created_by) + MODIFICATEURS successifs (redacteursDeclaration = JSON append-only).
    const redacteurInitial = sinistre?.redacteurInitialNom || sinistre?.createdBy || "—";
    const modificateurs = (() => {
        try {
            const arr = typeof sinistre?.redacteursDeclaration === "string"
                ? JSON.parse(sinistre.redacteursDeclaration) : sinistre?.redacteursDeclaration;
            return Array.isArray(arr) ? arr : [];
        } catch (e) { return []; }
    })();
    // Repli par défaut : si l'historique append-only est vide, on utilise updated_by/updated_at
    // (dernière modification connue) — utile pour les dossiers antérieurs au mécanisme.
    const modificateursEff = modificateurs.length
        ? modificateurs
        : ((sinistre?.updatedAt && sinistre.updatedAt !== sinistre?.createdAt && (sinistre?.updatedByNom || sinistre?.updatedBy))
            ? [{ nom: sinistre.updatedByNom || sinistre.updatedBy, date: sinistre.updatedAt }]
            : []);
    const dernierModif = modificateursEff.length ? modificateursEff[modificateursEff.length - 1] : null;
    const dossierMenuItems = [
        { key: "edit", label: "Modifier les informations", disabled: readOnly },
        { key: "type", label: "Changer le type de sinistre", disabled: readOnly },
        { key: "resp", label: "Définir la responsabilité", disabled: readOnly },
        // Carte Brune (BNCB) : marquer/retirer le dossier transfrontalier (gestion sinistre HSI027).
        ...(rightsService("HSI027") ? [{
            key: "carte-brune",
            label: sinistre?.carteBrune ? "🌍 Retirer « Carte Brune »" : "🌍 Marquer « Carte Brune » (BNCB)",
            disabled: readOnly,
        }] : []),
        ...((statut === "A" || statut === "RF") ? [{ key: "valider", label: "Valider le dossier", disabled: readOnly }] : []),
        ...(statut === "A" ? [{ key: "refuser", label: "Refuser le dossier", danger: true, disabled: readOnly }] : []),
        { type: "divider" },
        // Dossier clos → Réactiver / rouvrir (HSI104) ; sinon → Suspendre. Bloqué si lecture seule.
        dossierClos
            ? { key: "reouvrir", label: "Réactiver / réouvrir le dossier", disabled: readOnly || !rightsService("HSI104") }
            : { key: "suspendre", label: "Suspendre le dossier", disabled: readOnly },
        // Verrou lecture seule — réservé au Pool (HSI114).
        ...(peutVerrouiller ? [
            { type: "divider" },
            readOnly
                ? { key: "lever-lecture-seule", label: "🔓 Lever la lecture seule" }
                : { key: "lecture-seule", label: "🔒 Mettre en lecture seule", danger: true },
        ] : []),
    ];
    // Filet ceinture+bretelles : on n'écrase l'entête persistée QUE s'il existe une évaluation
    // normale FLAGGÉE `courant=true` (autorité de validation) — sa provision prime alors sur une
    // entête éventuellement écrasée par une édition de déclaration (cas réévaluation validée perdue).
    // Sinon (flag absent/cassé, ou entête posée hors-workflow) on GARDE l'entête `sinistre.evaluation`
    // pour ne pas changer l'affichage d'un dossier sans courante flaggée. Cohérent avec le backfill.
    const evaluationFlaggee = (evaluations || []).find((e) => e && e.courant === true) || null;
    const evaluationVal = Number(evaluationFlaggee?.nouvelleProvision ?? sinistre?.evaluation) || 0;
    const evaluationOuv = Number(sinistre?.evaluationOuverture) || 0;
    // SAP · disponible = valeur PERSISTÉE `sinistre.sap` (définition canonique {2,4,5}, partagée avec
    // /stats/sap-sinistres et l'état des survenus → cohérence totale). Dossier clos → 0.
    const sapVal = dossierClos ? 0 : (Number(sinistre?.sap) || 0);
    const regleVal = Number(montant) || 0;
    const payeVal = Number(montantPaye) || 0;
    const enCoursVal = montantEnCours;
    const recoursVal = (encaissements || []).reduce((a, d) =>
        (d?.etatDecaissement !== "REJETE" && d?.etatDecaissement !== "CHEQUE_ANNULE") ? a + (Number(d?.montantGlobal) || 0) : a, 0);
    const pct = (n) => evaluationVal > 0 ? Math.min(100, Math.max(0, (n / evaluationVal) * 100)) : 0;
    // Provision CONSOMMÉE = montant RÉGLÉ (chèque assigné) : c'est lui qui réduit le SAP disponible.
    // L'encaissé (payeVal) n'en est qu'un sous-ensemble → la barre le sous-évaluait (segment « Payé » à 0
    // alors que des règlements réglés existaient). On aligne la barre sur le « réglé/engagé ».
    const reglePct = pct(regleVal);
    const coursPct = pct(enCoursVal);
    // Réévaluation bloquée quand le dossier est clos ET que l'utilisateur n'a pas HSI104 — OU en lecture seule.
    const reevaluationBloquee = readOnly || (["T", "S", "F", "Rf", "RF"].includes(statut) && !rightsService("HSI104"));
    const peutCloturer = !readOnly && rightsService("HSI105");
    const dispoVal = dossierClos ? 0 : Math.max(0, evaluationVal - regleVal);
    const dispoPct = dossierClos ? 0 : Math.max(0, 100 - reglePct - coursPct);
    const engagePct = evaluationVal > 0 ? Math.round((regleVal / evaluationVal) * 100) : 0;
    const deltaOuv = evaluationVal - evaluationOuv;

    const nbVictimes = (victimes || []).length;
    // Nombre de victimes DÉCLARÉ (saisi à la déclaration) : peut dépasser le nombre réellement
    // enregistré tant que toutes les victimes ne sont pas saisies → la card affiche « créées / déclarées ».
    const nbVictimesDeclarees = Number(sinistre?.nombreVictimes) || 0;
    const nbAdversaires = (victimes || []).filter((v) => v?.adversaire === true || v?.personne?.adversaire === true).length;
    // Victimes réellement affichées dans la section Victimes : victimes propres du dossier
    // + parties adverses CORPORELLES/MIXTES (leurs blessés sont des victimes). On EXCLUT les
    // parties adverses purement matérielles. Aligne le libellé d'onglet et le KPI sur la liste.
    const nbVictimesAffichees = (victimes || []).filter((v) => {
        const estAdv = v?.adversaire === true || v?.personne?.adversaire === true;
        return !estAdv || v?.typeSinistre === "MIXTE" || v?.typeSinistre === "CORPOREL";
    }).length;
    const nbGrave = Number(sinistre?.nombreBlesseGrave) || 0;
    const nbLeger = Number(sinistre?.nombreBlesseLeger) || 0;
    const nbDeces = Number(sinistre?.nombreDeces) || 0;

    // Comptes RÉELLEMENT ENREGISTRÉS (victimes hors adversaires). La gravité (grave/léger)
    // est portée par la victime ; un blessé SANS gravité renseignée est compté en « léger »
    // (hypothèse la moins sévère → reste visible). On affiche ensuite MAX(déclaré, enregistré)
    // pour ne jamais sous-compter (ex. blessé enregistré alors que la déclaration = 0).
    const nonAdvVictimes = (victimes || []).filter((v) => !(v?.adversaire === true || v?.personne?.adversaire === true));
    const regBlesses = nonAdvVictimes.filter((v) => v?.blesseSinistre === true);
    const regGrave = regBlesses.filter((v) => v?.graviteBlessure === "GRAVE").length;
    const regLeger = regBlesses.filter((v) => v?.graviteBlessure !== "GRAVE").length;
    const regDeces = nonAdvVictimes.filter((v) => v?.decesSinistre === true).length;
    const graveCount = Math.max(nbGrave, regGrave);
    const legerCount = Math.max(nbLeger, regLeger);
    const decesCount = Math.max(nbDeces, regDeces);

    const valideDone = ["O", "I", "T", "S", "R"].includes(statut);
    const evalueDone = (evaluations?.length || 0) > 0 || evaluationVal > 0;
    const reglementStarted = (decaissements?.length || 0) > 0;
    const clotureDone = statut === "T";
    const stepCls = {
        decl: "done",
        valid: valideDone ? "done" : (statut === "A" || statut === "RF" ? "current" : "pending"),
        eval: evalueDone ? "done" : (valideDone ? "current" : "pending"),
        regl: clotureDone ? "done" : (reglementStarted ? "current" : "pending"),
        clot: clotureDone ? "done" : "pending",
    };

    const numeroSinistre = sinistre?.numeroSinistreM || sinistre?.numeroSinistreP || sinistre?.numSinistre || "—";
    const assureNom = `${sinistre?.contrat?.souscripteur?.nom || ""} ${sinistre?.contrat?.souscripteur?.prenom || ""}`.trim() || "—";
    const police = sinistre?.contrat?.numeroPoliceM || sinistre?.contrat?.numeroPoliceP || "—";
    const immat = sinistre?.engin?.immatriculation || "—";
    const categorie = sinistre?.engin?.categorie?.nom || sinistre?.engin?.categorie?.codeCategorie || "—";

    const benefName = (d) => {
        if (d?.beneficiaire?.nom) return `${d.beneficiaire.nom} ${d.beneficiaire.prenom || ""}`.trim();
        if (d?.beneficiaireMoral?.raisonSociale) return d.beneficiaireMoral.raisonSociale;
        if (d?.beneficiaireConstat?.nom) return d.beneficiaireConstat.nom;
        return "—";
    };
    const initials = (nm) => (nm || "").split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase() || "B";
    const totalNet = (decaissements || []).filter((d) => d?.etatDecaissement !== "REJETE")
        .reduce((a, d) => a + (Number(d?.montantGlobal) || 0), 0);

    // Le backend recrée une Personne (codeId neuf) par affectation → sinistre.experts contient
    // des doublons (une occurrence par victime affectée). On compte les experts UNIQUES par
    // identité (nom) pour les badges/compteurs, comme dans l'onglet Experts.
    const uniqueExpertsCount = useMemo(() => {
        const keys = new Set();
        (experts || []).forEach((e) => {
            const k = `${e?.nom || ""} ${e?.prenom || ""}`
                .normalize("NFD").replace(/[̀-ͯ]/g, "").trim().toUpperCase() || e?.codeId;
            if (k) keys.add(k);
        });
        return keys.size;
    }, [experts]);

    const timeline = useMemo(() => {
        const evs = [];
        if (sinistre?.dateSinistre) evs.push({ d: sinistre.dateSinistre, t: "Survenance déclarée", m: sinistre?.mobile ? "via application mobile" : "", k: "" });
        if (sinistre?.dateDeclaration && sinistre?.dateDeclaration !== sinistre?.dateSinistre)
            evs.push({ d: sinistre.dateDeclaration, t: "Dossier déclaré", m: "", k: "" });
        (evaluations || []).forEach((ev) => {
            if (ev?.createdAt) evs.push({ d: ev.createdAt, t: `Réévaluation — ${fmt(ev.ancienneProvision)} → ${fmt(ev.nouvelleProvision)} F`, m: ev.motifRevision ? `Motif : ${ev.motifRevision}` : "", k: "" });
        });
        (decaissements || []).forEach((d) => {
            if (d?.etatDecaissement === "ENCAISSE") evs.push({ d: d.dateReglement || d.createdAt, t: `Règlement encaissé — ${fmt(d.montantGlobal)} F`, m: benefName(d) !== "—" ? benefName(d) : "", k: "money" });
            else if (d?.etatDecaissement === "CHEQUE_ANNULE" && Number(d.montantGlobal) >= 0) evs.push({ d: d.updatedAt || d.dateReglement || d.createdAt, t: `Chèque annulé — N° ${d.numeroCheque || "—"}`, m: d.motifAnnulerCheque ? `Motif : ${d.motifAnnulerCheque}` : "", k: "warn" });
        });
        return evs.filter((e) => e.d).sort((a, b) => new Date(b.d) - new Date(a.d)).slice(0, 8);
    }, [sinistre, evaluations, decaissements]); // eslint-disable-line

    // ── Rendu du contenu de l'onglet actif ──
    const renderApercu = () => (
        <>
            <div className="dsv2-grid2">
                <div className="dsv2-card">
                    <div className="hd"><span className="t">Répartition financière</span></div>
                    <div className="bd">
                        <div className="dsv2-fin">
                            <div className="dsv2-frow"><span className="fl">Évaluation</span>
                                <div className="fbar"><i style={{ width: "100%", background: "var(--navy)" }}></i></div>
                                <span className="fv num">{fmt(evaluationVal)}</span></div>
                            {/* Réglé = règlements avec chèque assigné (en attente d'encaissement OU encaissés).
                                Il porte l'essentiel de la provision engagée : sans lui, les montants réglés
                                disparaissaient de la répartition (Évaluation ≠ somme des lignes).
                                « Payé » (encaissé) n'est PAS une ligne additive — c'est un SOUS-ENSEMBLE de
                                Réglé : le laisser ici laissait croire à un cumul. Il reste visible sur la
                                carte KPI « Payé » et dans la barre de consommation de la provision. */}
                            <div className="dsv2-frow"><span className="fl">Réglé</span>
                                <div className="fbar"><i style={{ width: `${pct(regleVal)}%`, background: "var(--navy-2)" }}></i></div>
                                <span className="fv num">{fmt(regleVal)}</span></div>
                            <div className="dsv2-frow"><span className="fl">En cours</span>
                                <div className="fbar"><i style={{ width: `${coursPct}%`, background: "var(--warn)" }}></i></div>
                                <span className="fv num">{fmt(enCoursVal)}</span></div>
                            <div className="dsv2-frow"><span className="fl">Disponible (SAP)</span>
                                <div className="fbar"><i style={{ width: `${pct(sapVal)}%`, background: "#C7CAE6" }}></i></div>
                                <span className="fv num">{fmt(sapVal)}</span></div>
                            <div className="dsv2-frow"><span className="fl">Recours encaissés</span>
                                <div className="fbar"><i style={{ width: `${pct(recoursVal)}%`, background: "#2563EB" }}></i></div>
                                <span className="fv num">{fmt(recoursVal)}</span></div>
                        </div>
                    </div>
                </div>

                <div className="dsv2-card">
                    <div className="hd"><span className="t">Circonstances</span>
                        {sinistre?.typeDegats && <span className="dsv2-pill navy" style={{ padding: "2px 8px" }}>{sinistre.typeDegats}</span>}</div>
                    <div className="bd">
                        <p className="dsv2-prose">{sinistre?.circonstance || "Aucune circonstance renseignée."}</p>
                        <div className="dsv2-kv">
                            <div className="row"><span className="lab">Type de dégâts</span><span className="val">{sinistre?.typeDegats || "—"}</span></div>
                            <div className="row"><span className="lab">Responsabilité assuré</span><span className="val num">{(sinistre?.tauxResponsabilite ?? "—")}{sinistre?.tauxResponsabilite != null ? "%" : ""}</span></div>
                            <div className="row"><span className="lab">Note vocale</span><span className="val">{sinistre?.noteVocale ? "Disponible" : "—"}</span></div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="dsv2-card">
                <div className="hd"><span className="t">Règlements récents</span>
                    <a onClick={() => setActiveTab("reglements")}>Tout voir ({decaissements?.length || 0})</a></div>
                <div className="bd" style={{ padding: "6px 6px 10px" }}>
                    {(decaissements?.length || 0) === 0 ? (
                        <div className="dsv2-empty">Aucun règlement pour ce dossier.</div>
                    ) : (
                        <div className="dsv2-twrap">
                            <table className="dsv2-rt">
                                <thead><tr>
                                    <th>Bénéficiaire</th><th style={{ textAlign: "right" }}>Montant</th>
                                    <th>Chèque</th><th>Date</th><th>État</th>
                                </tr></thead>
                                <tbody>
                                    {(decaissements || []).slice(0, 5).map((d) => {
                                        const neg = Number(d?.montantGlobal) < 0;
                                        const etat = ETAT_DEC[d?.etatDecaissement] || { label: d?.etatDecaissement || "—", cls: "default" };
                                        const nm = benefName(d);
                                        return (
                                            <tr key={d.codeId}>
                                                <td><div className="dsv2-benef">
                                                    <span className="av" style={neg ? { background: "var(--crit-tint)", color: "var(--crit)" } : {}}>{neg ? "↩" : initials(nm)}</span>
                                                    <div><div className="nm">{neg ? "Contre-passation" : nm}</div><div className="rg">Reg. {d?.numero || "—"}</div></div>
                                                </div></td>
                                                <td className={`dsv2-amt num ${neg ? "neg" : ""}`}>{neg ? "−" : ""}{fmt(Math.abs(Number(d?.montantGlobal) || 0))}</td>
                                                <td className="dsv2-cheque">{d?.numeroCheque || "—"} {d?.banque && <span className="bk">{d.banque}</span>}</td>
                                                <td className="num">{d?.dateReglement ? frenchDate(d.dateReglement) : "—"}</td>
                                                <td><span className={`dsv2-pill ${etat.cls}`}><span className="led"></span>{etat.label}</span></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot><tr className="dsv2-tfoot">
                                    <td>Total réglé (net)</td><td className="dsv2-amt num">{fmt(totalNet)}</td><td colSpan={3}></td>
                                </tr></tfoot>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <div className="dsv2-card">
                <div className="hd"><span className="t">Historique du dossier</span>
                    <a onClick={() => setActiveTab("historique")}>Tout voir</a></div>
                <div className="bd">
                    {timeline.length === 0 ? (
                        <div className="dsv2-empty">Aucun évènement enregistré.</div>
                    ) : (
                        <div className="dsv2-tl">
                            {timeline.map((e, i) => (
                                <div className={`dsv2-ev ${e.k}`} key={i}>
                                    <span className="em"></span>{i < timeline.length - 1 && <span className="eline"></span>}
                                    <div><div className="et">{e.t}</div>
                                        <div className="ed"><span className="num">{frenchDate(e.d)}</span>{e.m && <span>{e.m}</span>}</div></div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    );

    const renderSection = () => {
        switch (activeTab) {
            case "apercu":
                return renderApercu();
            case "declaration":
                return (<div className="dsv2-card"><div className="bd">
                    <Tabs items={[
                        { key: "attestation", label: "Attestation de déclaration", children: <div style={{ paddingTop: 8 }}><DeclarationTab sinistre={sinistre} /></div> },
                        { key: "fiche", label: "Fiche de déclaration", children: <div style={{ paddingTop: 8 }}><FicheDeclarationTab sinistre={sinistre} /></div> },
                    ]} />
                </div></div>);
            case "parties":
                return (<div className="dsv2-card"><div className="bd">
                    <Tabs items={[
                        { key: "victimes", label: `Victimes${nbVictimesAffichees ? ` (${nbVictimesAffichees})` : ""}`, children: <div style={{ paddingTop: 8 }}><VictimesTab sinistre={sinistre} victimes={victimes} setRefetch={setRefetch} editMode={editMode} setEditMode={setEditMode} variant="v2" /></div> },
                        { key: "adversaires", label: `Partie(s) adverse(s)${nbAdversaires ? ` (${nbAdversaires})` : ""}`, children: <div style={{ paddingTop: 8 }}><AdversairesTab sinistre={sinistre} victimes={victimes} setRefetch={setRefetch} variant="v2" /></div> },
                    ]} />
                </div></div>);
            case "evaluations":
                return (<SapTab sinistre={sinistre} evaluations={evaluations} garantiesContrat={garanties} onReevaluationClick={handleReevaluationClick} onChanged={() => setRefetch((r) => !r)} />);
            case "experts":
                return (<ExpertsTab sinistre={sinistre} experts={experts} expertises={expertises} setRefetch={setRefetch} editMode={editMode} setEditMode={setEditMode} />);
            case "prises":
                return (<PrisesEnChargeTab sinistre={sinistre} setEditMode={setEditMode} editMode={editMode} setRefetch={setRefetch} />);
            case "reglements":
                return (<ReglementsTabV2
                    sinistre={sinistre} decaissements={decaissements} evaluationCourante={evaluationCourante}
                    modalQuittance={modalQuittance} setModalQuittance={setModalQuittance} quittance={quittance} setQuittance={setQuittance}
                    modalFicheDemandeReglement={modalFicheDemandeReglement} setModalFicheDemandeReglement={setModalFicheDemandeReglement}
                    ficheDemandeReglement={ficheDemandeReglement} setFicheDemandeReglement={setFicheDemandeReglement}
                    modalOrdreDepense={modalOrdreDepense} setModalOrdreDepense={setModalOrdreDepense} ordreDepense={ordreDepense} setOrdreDepense={setOrdreDepense}
                    modalAssignCheque={modalAssignCheque} setModalAssignCheque={setModalAssignCheque}
                    modalRemiseCheque={modalRemiseCheque} setModalRemiseCheque={setModalRemiseCheque}
                    modalAnnulerCheque={modalAnnulerCheque} setModalAnnulerCheque={setModalAnnulerCheque}
                    decaissement={decaissement} setDecaissement={setDecaissement} montant={montant} setRefetch={setRefetch}
                    onDecaissementUpdated={handleDecaissementAfterUpdate} />);
            case "offres":
                return (<OffresSection sinistre={sinistre} expertises={expertises} />);
            case "documents":
                return (<DocumentsAFournir sinistre={sinistre} captured={captured} captures={captures}
                    uploadedPieces={sinistre?.pieces} documentsConfig={sinistre?.documentsAFournir} />);
            case "historique":
                return (<HistoriqueTab sinistre={sinistre} evaluations={evaluations} decaissements={decaissements} experts={experts} expertises={expertises} />);
            case "discussions":
                return (<DiscussionsTabV2 sinistre={sinistre} />);
            case "recours":
                return (<Recours evaluation={evaluation} sinistre={sinistre} selectedGaranties={selectedGaranties}
                    handleSuspensionDossier={handleSuspensionDossier} setIsRecours={setIsRecours} addItem={addItem}
                    encaissements={encaissements} handleClotureDossier={handleClotureDossier}
                    loadingCloture={loadingCloture} loadingSuspension={loadingSuspension}
                    modalCloture={modalCloture} modalEncaissement={modalEncaissement}
                    modalRecoursReevaluation={modalRecoursReevaluation} modalSuspension={modalSuspension}
                    onNameChange={onNameChange} setModalEncaissement={setModalEncaissement}
                    setModalRecoursReevaluation={setModalRecoursReevaluation}
                    showFicheDemandeReglement={setFicheDemandeReglement} showOrdreDepense={showOrdreDepense}
                    showQuittance={showQuittance} provisionGaranties={provisionGaranties}
                    setModalAssignCheque={setModalAssignCheque} setEvaluation={setEvaluations} garanties={garanties}
                    setProvisionGarantie={setProvisionGarantie} setSelectedGaranties={setSelectedGaranties}
                    setValueSelected={setValueSelected} name={name} handleChange={handleChange}
                    handleSubmit={handleSubmit} setModalCloture={setModalCloture}
                    editMode={editMode} initEvaluation={initEvaluation} items={items} valueSelected={valueSelected}
                    inputRef={inputRef} setModalSuspension={setModalSuspension} setGarantieState={setGarantieState}
                    setMotifRevisionState={setMotifRevisionState} setDecaissement={setDecaissements}
                    montant={montant} editEvaluation={editEvaluation} recoursEvaluations={recoursEvaluations}
                    onNouvelleReevaluation={() => openReeval(true)}
                    setRefetch={setRefetch}
                    setOpenEncaissement={setOpenEncaissement} loadingSinistre={loadingSinistre} />);
            case "commission":
                return (<CommissionTechniqueTab sinistre={sinistre} />);
            case "conseil":
                return (<ConseilJuridiqueTab sinistre={sinistre} setRefetch={setRefetch} />);
            case "investigateur":
                return (<InvestigateurTab sinistre={sinistre} setRefetch={setRefetch} />);
            default:
                return null;
        }
    };

    const TABS = [
        { key: "apercu", label: "Aperçu" },
        { key: "declaration", label: "Déclaration" },
        { key: "parties", label: "Parties", count: nbVictimes + nbAdversaires },
        { key: "evaluations", label: "Évaluations", count: evaluations?.length || 0 },
        { key: "experts", label: "Experts", count: uniqueExpertsCount },
        { key: "prises", label: "Prises en charge" },
        { key: "reglements", label: "Règlements", count: decaissements?.length || 0 },
        { key: "offres", label: "Offres" },
        { key: "documents", label: "Documents", count: sinistre?.pieces?.length || 0 },
        { key: "conseil", label: "Conseil juridique" },
        { key: "investigateur", label: "Investigateurs", count: sinistre?.investigateurs?.length || 0 },
        { key: "historique", label: "Historique" },
        { key: "discussions", label: "Discussions" },
        { key: "commission", label: "Commission Technique Sinistre" },
        { key: "recours", label: "Recours", count: encaissements?.length || 0 },
    ];

    const renderSearchResults = (results) => (
        <div className="px-3 py-2">
            <h6 className="text-muted text-uppercase mb-2">.</h6>
            {results?.map((item, index) => (
                <div key={index}
                    onClick={() => { setSearchValue(""); setSearchResults([]); navigate("/details-sinistre/v2", { state: { codeId: item.codeId } }); }}
                    className="dropdown-item d-flex align-items-center gap-2 py-1" style={{ cursor: "pointer" }}>
                    <span className="text-truncate">
                        {[(item?.numeroSinistreM || item?.numeroSinistreP || item?.numSinistre),
                        item?.numeroPoliceM ? item?.numeroPoliceM : item?.numeroPoliceP,
                        frenchDate(item?.dateSinistre), item?.immatriculation, item?.nom, item?.prenom || ""]
                            ?.map((text, i) => (
                                <React.Fragment key={i}>{i > 0 && " - "}{highlightMatch(text?.toString(), searchValue)}</React.Fragment>
                            ))}
                    </span>
                </div>
            ))}
        </div>
    );

    useEffect(() => {
        if (searchValue && searchValue?.length > 2) {
            searchSinistre(searchValue).then((res) => { setSearchResults(res.data.content); }).catch((err) => { console.log(err); });
        } else { setSearchResults([]); }
    }, [searchValue]);

    return (<div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
            <SideBar currentMenu={"SINISTRES"} />
            <div className="layout-page">
                <NavBar filteredResults={searchResults} setFilteredResults={setSearchResults}
                    renderSearchResults={renderSearchResults} searchValue={searchValue} setSearchValue={setSearchValue} />
                <div className="content-wrapper">
                    <div className="container-xxl flex-grow-1 container-p-y">
                        <style>{DSV2_CSS}</style>
                        <div className="dsv2">
                            <ContentieuxButton sinistreCodeId={sinistre?.codeId} />
                            <DetailsSinistreTour run={showTour} onClose={() => setShowTour(false)} />

                            {!codeId ? (
                                <div className="dsv2-loading">
                                    <div style={{ textAlign: "center", color: "#636A85" }}>
                                        <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Aucun dossier sélectionné</p>
                                        <p style={{ margin: 0 }}>Ouvrez un dossier sinistre depuis la liste pour l'afficher ici.</p>
                                    </div>
                                </div>
                            ) : !sinistre ? (
                                <div className="dsv2-loading"><Spin size="large" tip="Chargement du dossier…" /></div>
                            ) : (
                                <>
                                    {/* HERO */}
                                    <section className="dsv2-hero">
                                        <div className="idcol">
                                            <div className="dsv2-idline">
                                                <span className="dsv2-ref">{numeroSinistre}</span>
                                                <span className={`dsv2-pill ${st.cls}`}><span className="led"></span>{st.label}</span>
                                                {sinistre?.carteBrune && (
                                                    <span className="dsv2-chip" style={{ background: '#fff7e6', color: '#ad6800', border: '1px solid #ffd591' }}
                                                        title="Dossier transfrontalier — réglé sous la garantie CEDEAO, chèque au Bureau National de la Carte Brune (BNCB)">
                                                        🌍 Carte Brune (CEDEAO)
                                                    </span>
                                                )}
                                                {sinistre?.typeDegats && <span className="dsv2-chip">{sinistre.typeDegats}</span>}
                                                {sinistre?.mobile && <span className="dsv2-chip">📱 Mobile</span>}
                                                {readOnly && (
                                                    <span className="dsv2-chip" style={{ background: '#fff1f0', color: '#a8071a', border: '1px solid #ffa39e' }}
                                                        title="Dossier en lecture seule — gelé pour tout le monde (seul le Pool peut lever le verrou, HSI114)">
                                                        🔒 Lecture seule
                                                    </span>
                                                )}
                                                {(sinistre?.tauxResponsabilite !== null && sinistre?.tauxResponsabilite !== undefined) &&
                                                    <span className="dsv2-chip num">Resp. {sinistre.tauxResponsabilite}%</span>}
                                            </div>
                                            <h1>{assureNom}</h1>
                                            <div className="dsv2-subid">
                                                <span className="mono">{police}</span><span className="sep">·</span>
                                                <span>{categorie}</span><span className="sep">·</span>
                                                <span className="mono">{immat}</span><span className="sep">·</span>
                                                <span>Survenance {frenchDate(sinistre?.dateSinistre)}</span>
                                            </div>
                                            {/* Traçabilité rédaction : rédacteur initial + modifications successives. */}
                                            <div className="dsv2-subid" style={{ marginTop: 2 }}>
                                                <span>Déclaration prise par <b>{redacteurInitial}</b>{sinistre?.createdAt ? ` le ${frenchDate(sinistre.createdAt)}` : ""}</span>
                                                {modificateursEff.length > 0 && (
                                                    <>
                                                        <span className="sep">·</span>
                                                        <span title={modificateursEff.map((m) => `${m?.nom || m?.codeId || "—"}${m?.date ? ` (${frenchDate(m.date)})` : ""}`).join("  ;  ")}>
                                                            {modificateursEff.length} modification{modificateursEff.length > 1 ? "s" : ""}
                                                            {dernierModif ? <> — dernière : <b>{dernierModif?.nom || dernierModif?.codeId || "—"}</b>{dernierModif?.date ? ` le ${frenchDate(dernierModif.date)}` : ""}</> : null}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className="dsv2-actions">
                                            <button className="dsv2-btn" onClick={() => setShowTour(true)}>Guide ▸</button>
                                            <button className="dsv2-btn" onClick={openReeval} disabled={reevaluationBloquee}
                                                title={readOnly ? "Dossier en lecture seule" : (reevaluationBloquee ? `Dossier clôturé : réévaluation soumise à l'habilitation HSI104` : undefined)}>
                                                ＋ Réévaluer
                                            </button>
                                            <button className="dsv2-btn primary" onClick={confirmCloture} disabled={clotureDone || !peutCloturer}
                                                title={readOnly ? "Dossier en lecture seule" : (!peutCloturer ? "La clôture requiert l'habilitation HSI105" : undefined)}>Clôturer</button>
                                            <Dropdown menu={{ items: dossierMenuItems, onClick: onDossierMenu }} trigger={["click"]} placement="bottomRight">
                                                <button className="dsv2-btn">Gérer le dossier ▾</button>
                                            </Dropdown>
                                        </div>
                                    </section>

                                    {/* Bandeau LECTURE SEULE — dossier gelé pour tout le monde (Pool seul peut lever, HSI114). */}
                                    {readOnly && (
                                        <Alert type="error" showIcon style={{ margin: "0 0 12px" }}
                                            message="Dossier en lecture seule"
                                            description={
                                                <span>
                                                    Ce dossier est gelé : aucune modification n'est possible (règlement, expertise, prise en charge, victime, évaluation, discussion…).
                                                    {sinistre?.lectureSeuleDate ? <> Depuis le <b>{frenchDate(sinistre.lectureSeuleDate)}</b></> : null}
                                                    {sinistre?.lectureSeulePar ? <> par <b>{sinistre.lectureSeulePar}</b></> : null}
                                                    {sinistre?.lectureSeuleMotif ? <> — motif : <i>{sinistre.lectureSeuleMotif}</i></> : null}.
                                                    {peutVerrouiller ? " Vous pouvez lever le verrou depuis « Gérer le dossier »." : " Seul le Pool peut lever le verrou."}
                                                </span>
                                            } />
                                    )}

                                    {/* KPIs */}
                                    <section className="dsv2-kpis">
                                        <div className="dsv2-kpi accent"><div className="k-lab">Évaluation</div>
                                            <div className="k-val num">{fmt(evaluationVal)}<small>F</small></div>
                                            <div className="k-meta">{deltaOuv !== 0 ? <><span className="up">{deltaOuv > 0 ? "▲" : "▼"} {fmt(Math.abs(deltaOuv))}</span> depuis ouverture</> : "depuis l'ouverture"}</div></div>
                                        <div className="dsv2-kpi"><div className="k-lab">SAP · disponible</div>
                                            <div className="k-val num">{fmt(sapVal)}<small>F</small></div>
                                            <div className="k-meta num">{evaluationVal > 0 ? Math.round((sapVal / evaluationVal) * 100) : 0}% de la provision</div></div>
                                        <div className="dsv2-kpi"><div className="k-lab">Réglé</div>
                                            <div className="k-val num">{fmt(regleVal)}<small>F</small></div>
                                            <div className="k-meta num">{decaissements?.length || 0} règlement(s) avec chèque assigné</div></div>
                                        <div className="dsv2-kpi"><div className="k-lab">Payé</div>
                                            <div className="k-val num">{fmt(payeVal)}<small>F</small></div><div className="k-meta">encaissé</div></div>
                                        <div className="dsv2-kpi"><div className="k-lab">En cours</div>
                                            <div className="k-val num">{fmt(enCoursVal)}<small>F</small></div><div className="k-meta">validé, non encaissé</div></div>
                                        <div className="dsv2-kpi"><div className="k-lab">Recours</div>
                                            <div className="k-val num">{fmt(recoursVal)}<small>F</small></div>
                                            <div className="k-meta num">{encaissements?.length || 0} encaissement(s)</div></div>
                                    </section>

                                    {/* Reserve */}
                                    <section className="dsv2-reserve">
                                        <div className="dsv2-rtop">
                                            <span className="t">Consommation de la provision</span>
                                            <span className="q"><b className="num">{engagePct}%</b> engagé · reste <b className="num">{fmt(sapVal)} F</b></span>
                                        </div>
                                        <div className="dsv2-track" role="img" aria-label={`${Math.round(reglePct)}% réglé, ${Math.round(coursPct)}% en cours, ${Math.round(dispoPct)}% disponible`}>
                                            <span className="dsv2-seg-paye" style={{ width: `${reglePct}%` }}></span>
                                            <span className="dsv2-seg-cours" style={{ width: `${coursPct}%` }}></span>
                                            <span className="dsv2-seg-dispo" style={{ width: `${dispoPct}%` }}></span>
                                        </div>
                                        <div className="dsv2-legend">
                                            <span className="li"><span className="sw dsv2-seg-paye"></span>Réglé <b className="num">{fmt(regleVal)} F</b></span>
                                            <span className="li"><span className="sw dsv2-seg-cours"></span>En cours <b className="num">{fmt(enCoursVal)} F</b></span>
                                            <span className="li"><span className="sw dsv2-seg-dispo"></span>Disponible <b className="num">{fmt(dispoVal)} F</b></span>
                                        </div>
                                    </section>

                                    {/* BODY */}
                                    <div className="dsv2-body">
                                        <aside className="dsv2-rail">
                                            <div className="dsv2-card">
                                                <div className="hd"><span className="t">Identité du dossier</span></div>
                                                <div className="bd"><div className="dsv2-kv">
                                                    <div className="row"><span className="lab">Assuré</span><span className="val">{assureNom}</span></div>
                                                    <div className="row"><span className="lab">Police</span><span className="val mono">{police}</span></div>
                                                    <div className="row"><span className="lab">Immatriculation</span><span className="val mono">{immat}</span></div>
                                                    <div className="row"><span className="lab">Catégorie</span><span className="val">{categorie}</span></div>
                                                    <div className="row"><span className="lab">Survenance</span><span className="val num">{frenchDate(sinistre?.dateSinistre)}</span></div>
                                                    <div className="row"><span className="lab">Lieu</span><span className="val">{sinistre?.lieu || "—"}</span></div>
                                                    <div className="row"><span className="lab">Déclarant</span><span className="val">{sinistre?.nomDeclarant || "—"}</span></div>
                                                </div></div>
                                            </div>
                                            <div className="dsv2-card">
                                                <div className="hd"><span className="t">Cycle de vie</span></div>
                                                <div className="bd"><div className="dsv2-steps">
                                                    <div className={`dsv2-step ${stepCls.decl}`}><div className="marker"><span className="mk"></span><span className="line"></span></div>
                                                        <div><div className="s-t">Déclaré</div><div className="s-m">{frenchDate(sinistre?.dateDeclaration || sinistre?.dateSinistre)}{sinistre?.mobile ? " · via mobile" : ""}</div></div></div>
                                                    <div className={`dsv2-step ${stepCls.valid}`}><div className="marker"><span className="mk"></span><span className="line"></span></div>
                                                        <div><div className="s-t">{statut === "RF" ? "Refusé" : "Validé"}</div><div className="s-m">{valideDone ? "Dossier ouvert" : (statut === "A" ? "En attente" : "—")}</div></div></div>
                                                    <div className={`dsv2-step ${stepCls.eval}`}><div className="marker"><span className="mk"></span><span className="line"></span></div>
                                                        <div><div className="s-t">Évalué</div><div className="s-m num">{evalueDone ? `${fmt(evaluationVal)} F` : "—"}</div></div></div>
                                                    <div className={`dsv2-step ${stepCls.regl}`}><div className="marker"><span className="mk"></span><span className="line"></span></div>
                                                        <div><div className="s-t">En règlement</div><div className="s-m num">{reglementStarted ? `${decaissements.length} règlement(s)` : "—"}</div></div></div>
                                                    <div className={`dsv2-step ${stepCls.clot}`}><div className="marker"><span className="mk"></span></div>
                                                        <div><div className="s-t">Clôturé</div><div className="s-m">{clotureDone ? frenchDate(sinistre?.updatedAt) : "—"}</div></div></div>
                                                </div></div>
                                            </div>
                                            <div className="dsv2-card">
                                                <div className="hd"><span className="t">Parties</span><a onClick={() => setActiveTab("parties")}>Détails</a></div>
                                                <div className="bd"><div className="dsv2-parties">
                                                    {/* Ligne 1 : parties réellement CRÉÉES dans le dossier (fiches individuelles). */}
                                                    <div className="dsv2-pcell"><div className="n num">{nbVictimesDeclarees > nbVictimes ? `${nbVictimes} / ${nbVictimesDeclarees}` : nbVictimes}</div><div className="l">Victime(s)</div><span className="tg">{nbVictimesDeclarees > nbVictimes ? "Créées / décl." : "Créé(s)"}</span></div>
                                                    <div className="dsv2-pcell"><div className="n num">{nbAdversaires}</div><div className="l">Adversaire(s)</div><span className="tg">Créé(s)</span></div>
                                                    {/* Suite : MAX(déclaré, enregistré) → reflète la réalité sans jamais sous-compter. */}
                                                    <div className={`dsv2-pcell ${graveCount > 0 ? "warn" : ""}`}><div className="n num">{graveCount}</div><div className="l">Blessé(s) grave(s)</div><span className="tg">Total</span></div>
                                                    <div className={`dsv2-pcell ${decesCount > 0 ? "warn" : ""}`}><div className="n num">{decesCount}</div><div className="l">Décès</div><span className="tg">Total</span></div>
                                                    <div className="dsv2-pcell"><div className="n num">{legerCount}</div><div className="l">Blessé(s) léger(s)</div><span className="tg">Total</span></div>
                                                    <div className="dsv2-pcell"><div className="n num">{uniqueExpertsCount}</div><div className="l">Expert(s)</div><span className="tg">Enregistré</span></div>
                                                </div></div>
                                            </div>
                                        </aside>

                                        <section className="dsv2-main">
                                            <nav className="dsv2-tabbar" aria-label="Sections du dossier">
                                                {TABS.map((tb) => (
                                                    <button key={tb.key} data-tour-conseil={tb.key === 'conseil'} className={`dsv2-tab ${activeTab === tb.key ? "active" : ""}`} onClick={() => setActiveTab(tb.key)}>
                                                        {tb.label}{tb.count ? <span className="cnt num">{tb.count}</span> : null}
                                                    </button>
                                                ))}
                                            </nav>
                                            <Spin spinning={loadingSinistre}>
                                                <div className="dsv2-tabcontent">{renderSection()}</div>
                                            </Spin>
                                        </section>
                                    </div>

                                    {/* ── Réévaluation V2 ── */}
                                    <Modal
                                        title={reevalRecours ? "Nouvelle réévaluation de recours" : (editMode ? "Mettre à jour la réévaluation" : "Nouvelle réévaluation")}
                                        open={modalReevaluation}
                                        onCancel={closeReeval}
                                        onOk={submitReeval}
                                        okText={editMode ? "Mettre à jour" : "Enregistrer"}
                                        cancelText="Annuler"
                                        confirmLoading={loadingReeval}
                                        width={620}
                                        centered
                                    >
                                        <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 6 }}>
                                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                                <span className="dsv2-pill default" style={{ padding: "6px 12px" }}>Ancienne provision {reevalRecours ? "(recours)" : ""} :<b className="num" style={{ marginLeft: 6 }}>{fmt(reevalRecours ? (sinistre?.evaluationRecours || 0) : sinistre?.evaluation)} F</b></span>
                                                <span className="dsv2-pill ok" style={{ padding: "6px 12px" }}>Nouvelle provision :<b className="num" style={{ marginLeft: 6 }}>{fmt(totalGeneral)} F</b></span>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: 12.5, fontWeight: 600, color: "#636A85", display: "block", marginBottom: 6 }}>Garanties à évaluer</label>
                                                <Select mode="multiple" style={{ width: "100%" }} value={reevalSelected}
                                                    onChange={onGarantiesChange} options={reevalOptions} placeholder="Sélectionner les garanties à évaluer"
                                                    notFoundContent="Aucune garantie concernée n'est définie sur ce dossier." />
                                                {reevalOptions.length === 0 && (
                                                    <div style={{ fontSize: 12, color: "#b45309", marginTop: 6 }}>
                                                        Aucune garantie concernée n'est définie. Renseignez-les via « Modifier les garanties concernées » (onglet SAP/Évaluations) avant de réévaluer.
                                                    </div>
                                                )}
                                            </div>
                                            {(sousEvaluations || []).length > 0 && (
                                                <div className="dsv2-twrap">
                                                    <table className="dsv2-rt" style={{ minWidth: 520 }}>
                                                        <thead><tr>
                                                            <th>Garantie</th>
                                                            <th style={{ textAlign: "center" }}>Montant principal</th>
                                                            <th style={{ textAlign: "center" }}>Montant honoraires</th>
                                                            <th style={{ textAlign: "right" }}>Total</th>
                                                        </tr></thead>
                                                        <tbody>
                                                            {(sousEvaluations || []).map((s, i) => (
                                                                <tr key={s.garantie || i}>
                                                                    <td style={{ fontWeight: 600 }}>{s.garantie}</td>
                                                                    <td><InputNumber style={{ width: "100%" }} min={0} controls={false} placeholder="Montant principal"
                                                                        formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                                                                        parser={(v) => (v || "").replace(/\s/g, "")}
                                                                        onChange={(val) => onMontantChange(i, "montantPrincipal", val)} /></td>
                                                                    <td><InputNumber style={{ width: "100%" }} min={0} controls={false} placeholder="Montant honoraires"
                                                                        formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                                                                        parser={(v) => (v || "").replace(/\s/g, "")}
                                                                        onChange={(val) => onMontantChange(i, "montantHonoraires", val)} /></td>
                                                                    <td className="dsv2-amt num">{fmt(s.montantTotal)} F</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot><tr className="dsv2-tfoot">
                                                            <td colSpan={3} style={{ textAlign: "right" }}>Total général</td>
                                                            <td className="dsv2-amt num">{fmt(totalGeneral)} F</td>
                                                        </tr></tfoot>
                                                    </table>
                                                </div>
                                            )}
                                            <div>
                                                <label style={{ fontSize: 12.5, fontWeight: 600, color: "#636A85", display: "block", marginBottom: 6 }}>
                                                    Motif de la révision <span style={{ color: "#DC2626" }}>*</span></label>
                                                <Select style={{ width: "100%" }} placeholder="Sélectionner ou ajouter un motif"
                                                    value={evaluation?.motifRevision || undefined}
                                                    onChange={(value) => { handleChange(value, "motifRevision"); setMotifRevisionState(value && value.trim().length > 3 ? "success" : "error"); }}
                                                    options={items.map((it) => ({ label: it, value: it }))}
                                                    dropdownRender={(menu) => (<>{menu}<Divider style={{ margin: "8px 0" }} />
                                                        <Space style={{ padding: "0 8px 4px" }}>
                                                            <Input placeholder="Ajouter un motif" ref={inputRef} value={name} onChange={onNameChange} />
                                                            <Button type="text" icon={<PlusOutlined />} onClick={addItem}>Ajouter</Button>
                                                        </Space></>)} />
                                            </div>
                                            <div>
                                                <label style={{ fontSize: 12.5, fontWeight: 600, color: "#636A85", display: "block", marginBottom: 6 }}>Explication détaillée du motif</label>
                                                <Input.TextArea rows={3} placeholder="Veuillez fournir une explication détaillée du motif de la réévaluation…"
                                                    value={evaluation?.explicationMotif || ""}
                                                    onChange={(e) => setEvaluation({ ...evaluation, explicationMotif: e.target.value })} />
                                            </div>
                                            {rightsService("HSI116") && (
                                                <div>
                                                    <label style={{ fontSize: 12.5, fontWeight: 600, color: "#636A85", display: "block", marginBottom: 6 }}>Rattacher à un inventaire (optionnel)</label>
                                                    <InventaireSelectField value={reevalInventaireCodeId} onChange={setReevalInventaireCodeId} />
                                                </div>
                                            )}
                                        </div>
                                    </Modal>

                                    {/* ── Actions dossier (vrais endpoints, retour sur la V2) ── */}
                                    <EditModal
                                        visible={modalEdit}
                                        onCancel={() => setModalEdit(false)}
                                        sinistre={sinistre}
                                        loading={false}
                                        returnPath="/details-sinistre/v2"
                                    />
                                    <ResponsabilityModal
                                        sinistre={sinistre}
                                        visible={modalResponsabilite}
                                        onCancel={() => setModalResponsabilite(false)}
                                        onSave={() => { }}
                                        loading={actionLoading}
                                        setLoading={setActionLoading}
                                        initialValue={sinistre?.tauxResponsabilite}
                                        returnPath="/details-sinistre/v2"
                                    />
                                    <ChangeTypeModal
                                        sinistre={sinistre}
                                        visible={modalChangeType}
                                        onCancel={() => setModalChangeType(false)}
                                        onSave={() => { }}
                                        loading={actionLoading}
                                        setLoading={setActionLoading}
                                        currentType={sinistre?.typeSinistre}
                                        returnPath="/details-sinistre/v2"
                                    />
                                </>
                            )}
                        </div>
                    </div>
                    <Footer />
                    <div className="content-backdrop fade"></div>
                </div>
            </div>
        </div>
        <div className="layout-overlay layout-menu-toggle"></div>
        <div className="drag-target"></div>
    </div>);
};

export default DetailsSinistreV2;
